/**
 * Positions Settings Page (Phase 9.1)
 *
 * Manage organization positions with CRUD operations.
 * Features search, department filter, and modal form for create/edit.
 */

import { useState, useEffect } from 'react';
import {
  Briefcase,
  Plus,
  Search,
  AlertCircle,
  Loader2,
  Users,
  Pencil,
  Trash2,
  X,
  Building2,
  ChevronDown,
} from 'lucide-react';
import { usePositions, useDeletePosition, useCreatePosition, useUpdatePosition } from '../../hooks/usePositions';
import { useDepartmentLookup } from '../../hooks/useDepartments';
import { useCurrentUser } from '../../hooks/useUsers';
import type { PositionWithCount, CreatePosition } from '@guild/shared';
import { useDocumentTitle } from '../../lib/hooks/useDocumentTitle';

export function PositionsPage() {
  useDocumentTitle('Positions');
  // Phase 19.S frontend migration — read role from DB-canonical /api/me, not the JWT claim.
  const { data: currentUser } = useCurrentUser();
  const userRole = currentUser?.role;
  const isSuperAdmin = userRole === 'super_admin';
  const isAdmin = userRole === 'admin' || isSuperAdmin;

  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState<PositionWithCount | null>(null);

  const { data: positions, isLoading, error } = usePositions({
    search: searchQuery || undefined,
    departmentId: departmentFilter || undefined,
    includeInactive: showInactive,
  });

  const { data: departments } = useDepartmentLookup();
  const deleteMutation = useDeletePosition();

  const handleCreate = () => {
    setEditingPosition(null);
    setIsFormOpen(true);
  };

  const handleEdit = (position: PositionWithCount) => {
    setEditingPosition(position);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this position?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingPosition(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-guild-mint/50">
            <Briefcase size={24} className="text-guild-green" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              Positions
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Manage organization positions for grouping operators by job role
            </p>
          </div>
        </div>
        {isAdmin && (
          <button
            onClick={handleCreate}
            className="
              inline-flex items-center gap-2 rounded-xl
              bg-guild-green px-5 py-2.5
              text-sm font-semibold text-white
              shadow-lg shadow-guild-green/20
              hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
              active:scale-[0.98]
              transition-all duration-200
            "
          >
            <Plus size={18} strokeWidth={2.5} />
            Add Position
          </button>
        )}
      </div>

      {/* Filters Bar — only show when there is data or an active search/filter */}
      {((positions && positions.length > 0) || searchQuery || departmentFilter) && (
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[280px]">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search positions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="
                w-full rounded-xl border border-gray-200 bg-white
                pl-11 pr-4 py-2.5 text-sm
                placeholder:text-gray-400
                focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                transition-all duration-200
              "
            />
          </div>

          {/* Department filter */}
          <div className="relative">
            <Building2
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              className="
                appearance-none rounded-xl border border-gray-200 bg-white
                py-2.5 pl-9 pr-10
                text-sm text-gray-700
                transition-colors duration-200
                hover:border-gray-300 focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
              "
            >
              <option value="">All Departments</option>
              {departments?.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </select>
            <ChevronDown
              size={16}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
          </div>

          {/* Inactive filter */}
          {isAdmin && (
            <label className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl border border-gray-200 bg-white cursor-pointer hover:border-gray-300 transition-colors">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 bg-white text-guild-green focus:ring-guild-green focus:ring-offset-0"
              />
              <span className="text-sm font-medium text-gray-600">
                Show inactive
              </span>
            </label>
          )}
        </div>
      )}

      {/* Content */}
      {error ? (
        <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
          <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">
              Failed to load positions
            </p>
            <p className="text-sm text-red-600">
              Please try refreshing the page or contact support if the issue persists.
            </p>
          </div>
        </div>
      ) : isLoading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 size={32} className="text-guild-green animate-spin" />
          <p className="mt-4 text-sm text-gray-500">Loading positions...</p>
        </div>
      ) : positions && positions.length > 0 ? (
        <PositionList
          positions={positions}
          isAdmin={isAdmin}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isDeleting={deleteMutation.isPending}
        />
      ) : (
        <EmptyState
          searchQuery={searchQuery}
          isAdmin={isAdmin}
          onCreateClick={handleCreate}
        />
      )}

      {/* Form Dialog */}
      {isFormOpen && (
        <PositionForm
          position={editingPosition}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
}

// ============================================
// POSITION LIST COMPONENT
// ============================================

interface PositionListProps {
  positions: PositionWithCount[];
  isAdmin: boolean;
  onEdit: (position: PositionWithCount) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}

function PositionList({ positions, isAdmin, onEdit, onDelete, isDeleting }: PositionListProps) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/50">
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Position
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Code
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Department
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Operators
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Status
            </th>
            {isAdmin && (
              <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {positions.map((position) => (
            <tr
              key={position.id}
              className={`
                group transition-colors
                ${position.isActive ? 'hover:bg-gray-50/50' : 'bg-gray-50/30'}
              `}
            >
              {/* Name */}
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-3">
                  <div
                    className={`
                      flex h-9 w-9 items-center justify-center rounded-lg
                      ${position.isActive ? 'bg-purple-50' : 'bg-gray-100'}
                    `}
                  >
                    <Briefcase
                      size={16}
                      className={position.isActive ? 'text-purple-600' : 'text-gray-400'}
                    />
                  </div>
                  <span
                    className={`
                      text-sm font-semibold
                      ${position.isActive ? 'text-gray-900' : 'text-gray-400'}
                    `}
                  >
                    {position.name}
                  </span>
                </div>
              </td>

              {/* Code */}
              <td className="px-6 py-4 whitespace-nowrap">
                <code
                  className={`
                    text-xs font-mono px-2 py-1 rounded
                    ${position.isActive ? 'bg-gray-100 text-gray-700' : 'bg-gray-50 text-gray-400'}
                  `}
                >
                  {position.code}
                </code>
              </td>

              {/* Department */}
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`
                    text-sm
                    ${position.isActive ? 'text-gray-600' : 'text-gray-400'}
                  `}
                >
                  {position.departmentName || (
                    <span className="text-gray-300 italic">None</span>
                  )}
                </span>
              </td>

              {/* Operator Count */}
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <Users
                    size={16}
                    className={position.isActive ? 'text-gray-400' : 'text-gray-300'}
                  />
                  <span
                    className={`
                      text-sm font-medium
                      ${position.isActive ? 'text-gray-900' : 'text-gray-400'}
                    `}
                  >
                    {position.operatorCount}
                  </span>
                </div>
              </td>

              {/* Status */}
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`
                    inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold
                    ${position.isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-gray-100 text-gray-500'
                    }
                  `}
                >
                  <span
                    className={`
                      h-1.5 w-1.5 rounded-full
                      ${position.isActive ? 'bg-emerald-500' : 'bg-gray-400'}
                    `}
                  />
                  {position.isActive ? 'Active' : 'Inactive'}
                </span>
              </td>

              {/* Actions */}
              {isAdmin && (
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => onEdit(position)}
                      className="
                        inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5
                        text-sm font-medium text-gray-600
                        hover:bg-gray-100 hover:text-gray-900
                        transition-colors
                      "
                    >
                      <Pencil size={14} />
                      Edit
                    </button>
                    {position.isActive && (
                      <button
                        onClick={() => onDelete(position.id)}
                        disabled={isDeleting}
                        className="
                          inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5
                          text-sm font-medium text-red-600
                          hover:bg-red-50 hover:text-red-700
                          disabled:opacity-50 disabled:cursor-not-allowed
                          transition-colors
                        "
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================
// POSITION FORM COMPONENT
// ============================================

interface PositionFormProps {
  position: PositionWithCount | null;
  onClose: () => void;
}

function PositionForm({ position, onClose }: PositionFormProps) {
  const isEdit = !!position;
  const [name, setName] = useState(position?.name ?? '');
  const [code, setCode] = useState(position?.code ?? '');
  const [departmentId, setDepartmentId] = useState(position?.departmentId ?? '');
  const [description, setDescription] = useState(position?.description ?? '');
  const [error, setError] = useState<string | null>(null);

  const { data: departments } = useDepartmentLookup();
  const createMutation = useCreatePosition();
  const updateMutation = useUpdatePosition();

  const isPending = createMutation.isPending || updateMutation.isPending;

  // Reset form when position changes
  useEffect(() => {
    setName(position?.name ?? '');
    setCode(position?.code ?? '');
    setDepartmentId(position?.departmentId ?? '');
    setDescription(position?.description ?? '');
    setError(null);
  }, [position]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!code.trim()) {
      setError('Code is required');
      return;
    }

    if (!/^[a-z0-9_-]+$/i.test(code)) {
      setError('Code must be alphanumeric with underscores or hyphens');
      return;
    }

    try {
      if (isEdit && position) {
        await updateMutation.mutateAsync({
          id: position.id,
          data: {
            name: name.trim(),
            code: code.trim().toLowerCase(),
            departmentId: departmentId || undefined,
            description: description.trim() || undefined,
          },
        });
      } else {
        const data: CreatePosition = {
          name: name.trim(),
          code: code.trim().toLowerCase(),
          departmentId: departmentId || undefined,
          description: description.trim() || undefined,
        };
        await createMutation.mutateAsync(data);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-50">
              <Briefcase size={20} className="text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {isEdit ? 'Edit Position' : 'New Position'}
              </h2>
              <p className="text-xs text-gray-500">
                {isEdit ? 'Update position details' : 'Create a new position'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-5 flex items-center gap-2 rounded-xl bg-red-50 border border-red-100 px-4 py-3">
              <div className="h-2 w-2 rounded-full bg-red-500" />
              <p className="text-sm font-medium text-red-700">{error}</p>
            </div>
          )}

          <div className="space-y-5">
            {/* Name Field */}
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Filler Operator"
                className="
                  w-full rounded-xl border border-gray-200 bg-white
                  px-4 py-3 text-sm
                  placeholder:text-gray-400
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                  disabled:bg-gray-50 disabled:text-gray-500
                  transition-all duration-200
                "
                disabled={isPending}
                autoFocus
              />
            </div>

            {/* Code Field */}
            <div>
              <label
                htmlFor="code"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Code <span className="text-red-500">*</span>
              </label>
              <input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toLowerCase())}
                placeholder="e.g., filler"
                className="
                  w-full rounded-xl border border-gray-200 bg-white
                  px-4 py-3 text-sm font-mono
                  placeholder:text-gray-400
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                  disabled:bg-gray-50 disabled:text-gray-500
                  transition-all duration-200
                "
                disabled={isPending}
              />
              <p className="mt-1.5 text-xs text-gray-400">
                Short identifier for reports (letters, numbers, underscores, hyphens)
              </p>
            </div>

            {/* Department Field */}
            <div>
              <label
                htmlFor="departmentId"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Department
                <span className="font-normal text-gray-400 ml-1">(optional)</span>
              </label>
              <div className="relative">
                <select
                  id="departmentId"
                  value={departmentId}
                  onChange={(e) => setDepartmentId(e.target.value)}
                  disabled={isPending}
                  className="
                    w-full appearance-none rounded-xl border border-gray-200 bg-white
                    px-4 py-3 pr-10 text-sm
                    focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                    disabled:bg-gray-50 disabled:text-gray-500
                    transition-all duration-200
                  "
                >
                  <option value="">No department</option>
                  {departments?.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={16}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
              </div>
            </div>

            {/* Description Field */}
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Description
                <span className="font-normal text-gray-400 ml-1">(optional)</span>
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this position..."
                rows={3}
                className="
                  w-full rounded-xl border border-gray-200 bg-white
                  px-4 py-3 text-sm
                  placeholder:text-gray-400
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                  disabled:bg-gray-50 disabled:text-gray-500
                  resize-none
                  transition-all duration-200
                "
                disabled={isPending}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="
                px-5 py-2.5 text-sm font-semibold text-gray-600
                rounded-xl hover:bg-gray-100
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="
                inline-flex items-center gap-2 px-5 py-2.5
                text-sm font-semibold text-white bg-guild-green
                rounded-xl shadow-lg shadow-guild-green/20
                hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
                disabled:opacity-50 disabled:cursor-not-allowed
                active:scale-[0.98]
                transition-all duration-200
              "
            >
              {isPending && (
                <Loader2 size={16} className="animate-spin" />
              )}
              {isEdit ? 'Save Changes' : 'Create Position'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// EMPTY STATE COMPONENT
// ============================================

interface EmptyStateProps {
  searchQuery: string;
  isAdmin: boolean;
  onCreateClick: () => void;
}

function EmptyState({ searchQuery, isAdmin, onCreateClick }: EmptyStateProps) {
  const isSearching = !!searchQuery;

  return (
    <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-50 mb-5">
          <Briefcase size={32} className="text-gray-300" />
        </div>

        <h3 className="text-lg font-semibold text-gray-900">
          {isSearching ? 'No positions found' : 'No positions yet'}
        </h3>

        <p className="mt-2 text-sm text-gray-500 max-w-sm">
          {isSearching
            ? `No positions match "${searchQuery}". Try a different search term.`
            : 'Get started by creating your first position to organize operators by job role.'}
        </p>

        {isAdmin && !isSearching && (
          <button
            onClick={onCreateClick}
            className="
              mt-6 inline-flex items-center gap-2 rounded-xl
              bg-guild-green px-5 py-2.5
              text-sm font-semibold text-white
              shadow-lg shadow-guild-green/20
              hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
              active:scale-[0.98]
              transition-all duration-200
            "
          >
            <Plus size={18} strokeWidth={2.5} />
            Create First Position
          </button>
        )}
      </div>
    </div>
  );
}
